import { format, isValid, parseISO } from "date-fns";
import {
    NATIVE_INVOICE_LABEL_DEFAULTS,
    NATIVE_INVOICE_LABEL_KEYS,
    type NativeInvoiceLabelKey,
} from "../constants/nativeInvoice";
import { COUNTRIES, COUNTRIES_BY_CODE } from "../countries";
import type { NativeInvoiceRenderSnapshot } from "../types/nativeInvoice";
import { fullDateFullYearFormat } from "./date";

// Single source of truth for native-invoice document presentation, shared by
// the server-side PDF/HTML renderer and the React live preview so the two can
// never drift in money formatting, total-row visibility, address layout, or
// the logo-fallback monogram.

const DEFAULT_MONOGRAM = "K";

// `Intl.NumberFormat` construction is comparatively expensive, so cache one
// formatter per currency rather than building a new one on every call (a single
// invoice renders many money values). Keyed by normalized currency; the
// currency-less fallback formatter is cached under its own key.
const currencyFormatters = new Map<string, Intl.NumberFormat>();
const PLAIN_NUMBER_KEY = "__plain__";

function getCurrencyFormatter(normalizedCurrency: string): Intl.NumberFormat {
    const cached = currencyFormatters.get(normalizedCurrency);
    if (cached) {
        return cached;
    }
    const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: normalizedCurrency,
    });
    currencyFormatters.set(normalizedCurrency, formatter);
    return formatter;
}

function getPlainNumberFormatter(): Intl.NumberFormat {
    const cached = currencyFormatters.get(PLAIN_NUMBER_KEY);
    if (cached) {
        return cached;
    }
    const formatter = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    currencyFormatters.set(PLAIN_NUMBER_KEY, formatter);
    return formatter;
}

/**
 * Format a monetary amount for an invoice document. Uses the invoice's own
 * currency so the server PDF and the client preview always agree, regardless
 * of which side renders. Falls back to a plain number + code for currencies
 * `Intl` cannot resolve.
 */
export function formatInvoiceMoney(amount: number, currency: string): string {
    const normalizedCurrency = currency.toUpperCase();
    try {
        return getCurrencyFormatter(normalizedCurrency).format(amount);
    } catch {
        return `${getPlainNumberFormatter().format(amount)} ${normalizedCurrency}`;
    }
}

/**
 * Format a monetary amount when the currency is unknown — audit-log diffs only
 * carry the raw decimal. Keeps the thousands separator and two-decimal
 * normalisation of `formatInvoiceMoney` so the two never look unrelated.
 */
export function formatInvoiceAmount(amount: number): string {
    return getPlainNumberFormatter().format(amount);
}

/**
 * Quantities render as integers when whole, otherwise with two decimals, to
 * match the line-item presentation in the preview.
 */
export function formatInvoiceQuantity(quantity: number | null): string {
    if (quantity == null) {
        return "";
    }
    return Number.isInteger(quantity)
        ? quantity.toString()
        : quantity.toFixed(2);
}

/**
 * Format an ISO date (yyyy-MM-dd) as a human-friendly invoice date. Invalid or
 * empty values pass through unchanged so a manually entered string is never
 * silently dropped.
 */
export function formatInvoiceDate(value: string | null | undefined): string {
    if (!value) {
        return "";
    }
    const parsed = parseISO(value);
    if (!isValid(parsed)) {
        return value;
    }
    return format(parsed, fullDateFullYearFormat);
}

export interface InvoiceTotals {
    subtotal: number;
    total: number;
    tax: number;
    discount: number;
    deposit: number;
}

export interface InvoiceTotalsVisibility {
    showSubtotal: boolean;
    showTax: boolean;
    showDiscount: boolean;
    showDeposit: boolean;
}

/**
 * Determine which total rows to render. Subtotal is hidden when it equals the
 * grand total (no adjustments), and the adjustment rows are hidden when zero
 * (a negative tax/discount/deposit still prints).
 */
export function getInvoiceTotalsVisibility({
    subtotal,
    total,
    tax,
    discount,
    deposit,
}: InvoiceTotals): InvoiceTotalsVisibility {
    return {
        showSubtotal: subtotal !== total,
        showTax: tax !== 0,
        showDiscount: discount !== 0,
        showDeposit: deposit !== 0,
    };
}

/**
 * Derive the two-letter monogram used as a logo fallback: the first letter of
 * each of the first two words (e.g. "Internet Money" -> "IM"), or the first two
 * letters of a single-word name (e.g. "Acme" -> "AC"). Falls back to "K" when
 * no usable name is present.
 */
export function deriveInvoiceMonogram(name: string | null | undefined): string {
    const trimmed = name?.trim();
    if (!trimmed) {
        return DEFAULT_MONOGRAM;
    }
    const words = trimmed.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
        return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
    return words[0].slice(0, 2).toUpperCase();
}

/**
 * Build the download/attachment filename for a native invoice, e.g.
 * "Internet Money Invoice INV-100.pdf". The sending company leads (the file is
 * handed to a client, so it should identify the sender) and the literal
 * "Invoice" makes the document type obvious. Missing parts are dropped so a
 * draft without a company or number still produces a sensible name
 * ("Invoice INV-100.pdf", "Internet Money Invoice.pdf", or "Invoice.pdf").
 * Shared so the download, the saved-to-Documents copy, and the email
 * attachment all use one identical name.
 */
export function buildNativeInvoiceFileName(
    snapshot: NativeInvoiceRenderSnapshot,
): string {
    const parts = [
        snapshot.from.businessName?.trim(),
        "Invoice",
        snapshot.invoiceNumber?.trim(),
    ].filter((part): part is string => Boolean(part));
    return `${parts.join(" ")}.pdf`;
}

// Custom labels for the address rows that normally collapse into shared
// lines (city/state/postalCode merge into one "Austin, TX 78701" line;
// country always trails). Populated from field-settings labels via
// `applyNativeInvoicePdfFieldSettings`'s `fromAddressLabels`/`toAddressLabels`.
export interface InvoiceAddressLineLabels {
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
}

// Prefixes a non-empty label onto a non-empty value ("Label value"); returns
// the bare value when no label is set, or null when the value itself is
// empty. Avoids nested ternaries at each address-line call site below.
function labeledLineOrNull(
    value: string | null | undefined,
    label: string,
): string | null {
    if (!value) {
        return null;
    }
    return label ? `${label} ${value}` : value;
}

/**
 * Build the ordered, non-empty address lines for an invoice party block. Street
 * and street2 each get their own line; city/state/postal collapse into one line
 * and country trails. Shared so the preview and PDF render identical blocks.
 *
 * When the caller supplies a custom label for city, state, or postalCode, that
 * field (and its siblings) breaks out of the merged line into its own labeled
 * line - same treatment as street1/street2 - since merging a labeled value
 * into a shared line would either bury the label or garble the output.
 * Unlabeled fields stay merged when none of the group has a label, preserving
 * the pre-existing compact layout. Country renders as its own labeled line
 * (or bare) independently of the city/state/postalCode grouping.
 */
export function getInvoiceAddressLines(
    address:
        | {
              street?: string | null;
              street2?: string | null;
              city?: string | null;
              state?: string | null;
              postalCode?: string | null;
              country?: string | null;
          }
        | null
        | undefined,
    labels?: InvoiceAddressLineLabels,
): string[] {
    if (!address) {
        return [];
    }
    const state = isKnownNonUsInvoiceCountry(address.country)
        ? null
        : address.state;
    const cityLabel = labels?.city?.trim() ?? "";
    const stateLabel = labels?.state?.trim() ?? "";
    const postalLabel = labels?.postalCode?.trim() ?? "";
    const countryLabel = labels?.country?.trim() ?? "";
    const countryName = invoiceCountryDisplayName(address.country) || null;
    const countryLine = labeledLineOrNull(countryName, countryLabel);

    if (cityLabel || stateLabel || postalLabel) {
        return [
            address.street,
            address.street2,
            labeledLineOrNull(address.city, cityLabel),
            labeledLineOrNull(state, stateLabel),
            labeledLineOrNull(address.postalCode, postalLabel),
            countryLine,
        ].filter((line): line is string => Boolean(line));
    }

    const cityState = [address.city, state]
        .filter((part): part is string => Boolean(part))
        .join(", ");
    const cityLine = [cityState, address.postalCode]
        .filter((part): part is string => Boolean(part))
        .join(" ")
        .trim();
    return [
        address.street,
        address.street2,
        cityLine || null,
        countryLine,
    ].filter((line): line is string => Boolean(line));
}

export function invoiceCountryCode(country: string | null | undefined): string {
    if (!country) {
        return "";
    }
    if (COUNTRIES_BY_CODE[country]) {
        return country;
    }
    const upper = country.toUpperCase();
    if (COUNTRIES_BY_CODE[upper]) {
        return upper;
    }
    return (
        COUNTRIES.find(
            (entry) => entry.name === country || entry.code3 === upper,
        )?.code ?? ""
    );
}

export function invoiceCountryDisplayName(
    country: string | null | undefined,
): string {
    if (!country) {
        return "";
    }
    const code = invoiceCountryCode(country);
    return (code && COUNTRIES_BY_CODE[code]) || country;
}

export function isUsInvoiceCountry(
    country: string | null | undefined,
): boolean {
    return invoiceCountryCode(country) === "US";
}

/**
 * Whether the country resolves to a *known* non-US country. State is a
 * US-specific address concept, so it is dropped from the rendered block and the
 * saved payload only for known non-US countries. Empty or unrecognized
 * (legacy/free-text) country values return `false` so a stored state is
 * preserved rather than silently discarded — keeping the render and save paths
 * consistent.
 */
export function isKnownNonUsInvoiceCountry(
    country: string | null | undefined,
): boolean {
    const code = invoiceCountryCode(country);
    return code !== "" && code !== "US";
}

/**
 * Legacy client-side download name ("Invoice-<number>.pdf"), still used by the
 * frontend blob-save path which names the file before the response headers are
 * available. Server-produced copies (download disposition, email attachment,
 * Documents archive) use `buildNativeInvoiceFileName` instead.
 */
export function getNativeInvoicePdfFileName(
    invoiceNumber: string | null | undefined,
): string {
    const trimmed = invoiceNumber?.trim();
    const namePart = trimmed && trimmed.length > 0 ? trimmed : "draft";
    return `Invoice-${namePart}.pdf`;
}

// Resolves the full set of PDF field labels: an overridden key wins,
// everything else falls back to NATIVE_INVOICE_LABEL_DEFAULTS. Preview and
// PDF both call this through renderNativeInvoiceDocument.
export function resolveNativeInvoiceLabels(
    labelOverrides:
        | Partial<Record<NativeInvoiceLabelKey, string>>
        | null
        | undefined,
): Record<NativeInvoiceLabelKey, string> {
    const resolved: Record<NativeInvoiceLabelKey, string> = {
        ...NATIVE_INVOICE_LABEL_DEFAULTS,
    };
    if (!labelOverrides) {
        return resolved;
    }
    for (const key of NATIVE_INVOICE_LABEL_KEYS) {
        const override = labelOverrides[key];
        if (override) {
            resolved[key] = override;
        }
    }
    return resolved;
}
